import { createSlice } from "@reduxjs/toolkit";
import { FormattedMessage } from "react-intl";
import { openSnackbar } from "store/slices/snackbar";
import axios from "utils/axios";
import { getErrorCode, openSnackbarWithError } from "utils/errors";
import {
  fromBackToFrontAlbum,
  fromFrontToBackAlbum,
} from "views/albums/album-utils";
import { dispatch } from "../index";

const initialState = {
  error: null,
  // ==== albums ====
  albums: [],
  isLoading: true,
  isPosting: false,
  postingError: null,
  // ==== album ====
  albumPosted: null,
  album: null,
  isLoadingAlbum: true,
  displayAlert: false,
  scriptStatus: {
    is_correct_identifications_running: false,
    nb_identifications_to_process: 0,
    nb_photos_to_process: 0,
    isAvailable: false,
  },
  // ==== User subscriptions ====,
  isLoadingUserSubscriptions: false,
  userSubscriptions: [],
  countMailNotSent: 0,
  isDispatchingUserSubscriptionEmail: false,
  mailHasBeenDispatched: false,
  // ==== Tags ====
  tags: [],
  isLoadingTags: false,
  // Handle Pin
  isPinning: false,

  // ==== Orders ====
  orders: {
    isLoading: true,
    data: [],
  },
};

const slice = createSlice({
  name: "album",
  initialState,
  reducers: {
    resetAlbum(state) {
      state.album = null;
    },

    startLoading(state, action) {
      state.album = null;
      state.isLoading = true;
    },

    startPosting(state, action) {
      state.isPosting = true;
      state.photos = [];
    },

    // ===== ALBUMS =====
    getAlbums(state) {
      state.isLoading = true;
    },

    getAlbumsSuccess(state, action) {
      state.albums = action.payload.data;
      state.totalAlbumsCount = action.payload.total_count;
      state.isLoading = false;
    },

    getAlbumsFailure(state) {
      state.isLoading = false;
    },

    // ==== album ====

    getAlbum(state) {
      state.album = null;
      state.isLoadingAlbum = true;
    },

    getAlbumSuccess(state, action) {
      const hadPendingAnalysis = state.album
        ? state.album.has_pending_analysis
        : false;
      const hasPendingAnalysis = action.payload.has_pending_analysis;
      const hadPendingFaceAnalysis = state.album
        ? state.album.is_correct_identifications_running
        : false;
      const hasPendingFaceAnalysis =
        action.payload.is_correct_identifications_running;
      if (
        (hadPendingAnalysis && !hasPendingAnalysis) ||
        (hadPendingFaceAnalysis && !hasPendingFaceAnalysis)
      ) {
        state.displayAlert = true;
      } else {
        state.displayAlert = false;
      }
      state.album = fromBackToFrontAlbum(action.payload);
      state.has_pending_analysis = state.album.has_pending_analysis;
      state.isLoadingAlbum = false;
    },

    getAlbumFailure(state) {
      state.album = null;
      state.isLoadingAlbum = false;
    },

    createAlbumSuccess(state, action) {
      state.isPosting = false;
      state.albumPosted = fromBackToFrontAlbum(action.payload);
      state.postingError = null;
    },

    createAlbumFailure(state, action) {
      state.isPosting = false;
      state.postingError = action.payload;
    },

    editAlbumSuccess(state, action) {
      state.isPosting = false;
      state.album = fromBackToFrontAlbum(action.payload);
      state.postingError = null;
    },

    editAlbumFailure(state, action) {
      state.isPosting = false;
      state.postingError = action.payload;
    },

    // ====== UPLOAD IN PROGRESS ======
    syncUploadProgress(state, action) {
      state.album.has_pending_analysis = true;
      state.album.presigned_count =
        state.album.presigned_count + action.payload;
    },

    // ====== USER SUBSCRIPTIONS ======

    fetchUserSubscriptions(state) {
      state.isLoadingUserSubscriptions = true;
    },

    fetchUserSubscriptionsSuccess(state, action) {
      state.isLoadingUserSubscriptions = false;
      state.userSubscriptions = action.payload.data;
      state.countMailNotSent = action.payload.count_mail_not_sent;
    },

    fetchUserSubscriptionsCountFailure(state) {
      state.isLoadingUserSubscriptions = false;
    },

    dispatchUserSubscriptionEmail(state) {
      state.isDispatchingUserSubscriptionEmail = true;
    },

    dispatchUserSubscriptionEmailSuccess(state) {
      state.isDispatchingUserSubscriptionEmail = false;
      state.mailHasBeenDispatched = true;
    },

    dispatchUserSubscriptionEmailFailure(state) {
      state.isDispatchingUserSubscriptionEmail = false;
      state.mailHasBeenDispatched = false;
    },

    removedAlert(state) {
      state.displayAlert = false;
    },

    startCorrectIdentificationService(state) {
      state.album.is_correct_identifications_running = true;
    },

    startCorrectIdentificationServiceSuccess(state) {
      state.album.is_correct_identifications_running = true;
    },

    startCorrectIdentificationServiceFailure(state) {
      state.album.is_correct_identifications_running = false;
    },

    // ==== set pinned photo ====
    setPinnedPhoto(state, action) {
      state.album.isPinning = true;
    },

    setPinnedPhotoSuccess(state, action) {
      state.album.isPinning = false;
      state.album.main_photo_key = action.payload.photoKey;
    },

    setPinnedPhotoFailure(state, action) {
      state.album.isPinning = false;
    },

    // ==== orders ====
    getAlbumOrders(state) {
      state.orders.isLoading = true;
    },

    getAlbumOrdersSuccess(state, action) {
      state.orders.isLoading = false;
      state.orders.data = action.payload;
    },

    getAlbumOrdersFailure(state) {
      state.orders.isLoading = false;
    },

    scriptAvailableStatus(state, action) {
      console.log("scriptAvailableStatus", action.payload);
      state.scriptStatus = action.payload;
    },
  },
});

export default slice.reducer;

export function getAlbums(nameStartsWith, offset, limit = 100) {
  return async () => {
    try {
      dispatch(slice.actions.getAlbums());
      const response = await axios.get("/api/v1/photograph_albums", {
        params: {
          name_starts_with: nameStartsWith,
          offset,
          limit,
        },
      });
      dispatch(slice.actions.getAlbumsSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.getAlbumsFailure());
    }
  };
}

export function getAlbum(id, withRefresh = false) {
  return async (dispatch, getState) => {
    try {
      if (!withRefresh) {
        dispatch(slice.actions.getAlbum());
      }
      const response = await axios.get(`/api/v1/photograph_albums/${id}`);
      if (
        withRefresh &&
        !response.data.is_correct_identifications_running &&
        getState().albums.album &&
        getState().albums.album.is_correct_identifications_running
      ) {
        dispatch(
          openSnackbar({
            open: true,
            message:
              "Le script de reconnaissance vient de s'éxécuter avec succès...",
            variant: "alert",
            alert: {
              color: "success",
            },
            close: true,
          })
        );
      }
      dispatch(slice.actions.getAlbumSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.getAlbumFailure());
    }
  };
}

export function getScriptStatus(albumId) {
  return async () => {
    try {
      const response = await axios.get(
        `/api/v1/photograph_albums/${albumId}/script`
      );
      dispatch(
        slice.actions.scriptAvailableStatus({
          ...response.data,
          isAvailable:
            !response.data.is_correct_identifications_running &&
            response.data.nb_identifications_to_process > 0,
        })
      );
    } catch (error) {
      console.log(error);
    }
  };
}

export function createAlbum(album, navigate) {
  return async () => {
    try {
      dispatch(slice.actions.startPosting());
      const response = await axios.post(
        `/api/v1/photograph_albums`,
        fromFrontToBackAlbum(album)
      );
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="album.created" />,
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
      dispatch(slice.actions.createAlbumSuccess(response.data));
      navigate(`/dashboard/albums/${response.data.id}/photos`, {
        replace: true,
      });
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.createAlbumFailure(getErrorCode(error)));
    }
  };
}

export function editAlbum(album, navigate, albumId) {
  return async () => {
    try {
      dispatch(slice.actions.startPosting());
      const response = await axios.put(
        `/api/v1/photograph_albums/${albumId}`,
        fromFrontToBackAlbum(album)
      );
      dispatch(slice.actions.editAlbumSuccess(response.data));
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="album.updated" />,
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
      navigate(`/dashboard/albums/${response.data.id}`);
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.editAlbumFailure(getErrorCode(error)));
    }
  };
}

export function deleteAlbum(album, navigate) {
  return async () => {
    try {
      await axios.delete(`/api/v1/photograph_albums/${album.id}`);
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="album.deleted" />,
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
      navigate("/dashboard/albums");
    } catch (error) {
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="general.error.delete" />,
          variant: "alert",
          alert: {
            color: "error",
          },
          close: false,
        })
      );
      console.error(error);
    }
  };
}

export function syncUploadProgress(nbPhotos) {
  return async () => {
    dispatch(slice.actions.syncUploadProgress(nbPhotos));
  };
}

export function fetchUserSubscriptionsCount(albumId) {
  return async () => {
    try {
      dispatch(slice.actions.fetchUserSubscriptions());
      const response = await axios.get(
        `/api/v1/photograph_albums/${albumId}/user_subscriptions`
      );
      dispatch(slice.actions.fetchUserSubscriptionsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.fetchUserSubscriptionsCountFailure(error));
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="general.error.fetch" />,
          variant: "alert",
          alert: {
            color: "error",
          },
          close: false,
        })
      );
      console.error(error);
    }
  };
}

export function fetchUserSubscriptions(albumId) {
  return async () => {
    try {
      dispatch(slice.actions.fetchUserSubscriptions());
      const response = await axios.get(
        `/api/v1/photograph_albums/${albumId}/user_subscriptions`
      );
      dispatch(slice.actions.fetchUserSubscriptionsSuccess(response.data));
    } catch (error) {
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="general.error.fetch" />,
          variant: "alert",
          alert: {
            color: "error",
          },
          close: false,
        })
      );
      console.error(error);
    }
  };
}

export function dispatchUserSubscriptionEmails(albumId) {
  return async () => {
    try {
      dispatch(slice.actions.dispatchUserSubscriptionEmail());
      await axios.post(
        `/api/v1/photograph_albums/${albumId}/user_subscriptions/launch-mail`
      );
      dispatch(slice.actions.dispatchUserSubscriptionEmailSuccess());
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="user_subscription.success" />,
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
      setTimeout(() => {
        dispatch(fetchUserSubscriptionsCount(albumId));
      }, 8000);
    } catch (error) {
      dispatch(slice.actions.dispatchUserSubscriptionEmailFailure());
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="errors.unknown" />,
          variant: "alert",
          alert: {
            color: "error",
          },
          close: false,
        })
      );
      console.error(error);
    }
  };
}

export function removedAlert(albumId) {
  return async () => {
    dispatch(slice.actions.removedAlert());
  };
}

export function startCorrectIdentificationService(albumId) {
  return async () => {
    dispatch(slice.actions.startCorrectIdentificationService());
    try {
      await axios.post(
        `/api/v1/photograph_albums/${albumId}/start-correct-identification-service`
      );
      dispatch(slice.actions.startCorrectIdentificationServiceSuccess());
    } catch (error) {
      console.error(error);
      openSnackbarWithError(error, dispatch);
      dispatch(slice.actions.startCorrectIdentificationServiceFailure());
    }
  };
}

export function setPinnedPhoto(albumId, photoKey) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setPinnedPhoto(photoKey));
      await axios.post(
        `/api/v1/photograph_albums/${albumId}/photos/${photoKey}/pin`
      );
      dispatch(
        slice.actions.setPinnedPhotoSuccess({
          photoKey: photoKey,
        })
      );
      dispatch(
        openSnackbar({
          open: true,
          message: "Nouvelle photo épinglée !",
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
    } catch (error) {
      dispatch(slice.actions.setPinnedPhotoFailure());
      openSnackbarWithError(error, dispatch);
    }
  };
}

export function getAlbumOrders(albumid) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.getAlbumOrders());
      const response = await axios.get(
        `/api/v1/photograph_albums/${albumid}/orders`
      );
      dispatch(slice.actions.getAlbumOrdersSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getAlbumOrdersFailure());
      openSnackbarWithError(error, dispatch);
    }
  };
}
